/* eslint-disable camelcase */
import { MapiResponse } from '@mapbox/mapbox-sdk/lib/classes/mapi-response';
import geocodeClient, {
  GeocodeFeature, GeocodeQueryType, GeocodeRequest, GeocodeResponse,
} from '@mapbox/mapbox-sdk/services/geocoding';
import {
  compact, map, sortBy, uniq,
} from 'lodash';

// Ridiculous that Mapbox's API response types don't include anything that actually looks
// like the response
interface FeatureContext extends GeocodeFeature {
  short_code: string;
  wikidata: string;
}

const mapBoxApi = geocodeClient({ accessToken: (process.env.MAPBOX_TOKEN || 'pk.eyJ1Ijoiam9yZGFuZGVyc29uIiwiYSI6Im1qeHJrV2MifQ.fw00EERfusQnRenkHIrw8w') as string });

export const locationSearch = async (
  placeSearchString: string,
  types: GeocodeQueryType[] = ['place', 'locality', 'neighborhood'],
): Promise<GeocodeFeature[]> => {
  if (!placeSearchString) {
    return [];
  }

  const query = placeSearchString.replaceAll(';', '');
  if (!query) {
    return [];
  }

  const geocodeAPIBody: GeocodeRequest = {
    autocomplete: true,
    query,
    types,
    limit: 10,
  };
  const result: MapiResponse<GeocodeResponse> = await mapBoxApi.forwardGeocode(
    geocodeAPIBody,
  ).send();

  const getContextItem = (
    feature: GeocodeFeature, type: string,
  ) => (feature.context as FeatureContext[]).find((c) => c.id.match(type));

  const getCityFromFeature = (feature: GeocodeFeature) => getContextItem(feature, 'place')?.text;

  const getCountryFromFeature = (feature: GeocodeFeature) => {
    const countryContext = getContextItem(feature, 'country');
    if (!countryContext?.short_code) {
      return undefined;
    }

    const isUSorCanada = ['ca', 'us'].includes(countryContext.short_code);
    if (isUSorCanada || !countryContext?.text) {
      return undefined;
    }
    return countryContext.text;
  };

  const getStateFromFeature = (feature: GeocodeFeature, country?: string) => {
    // If there is a country, that means it must be outside the US/Canada, so we exclude the state
    if (country) {
      return undefined;
    }

    const stateContext = getContextItem(feature, 'region');
    if (!stateContext?.short_code) {
      return undefined;
    }

    return {
      shortName: stateContext.short_code.split('-')[1],
      fullName: stateContext.text.replace('District of Columbia', 'DC'),
    };
  };

  /** formatPlaceName converts a Mapbox feature into a nice place name.
   * It includes the name of the place or POI, the city (if available), the state abbreviation
   * (for the US and Canada only), and the country (if the feature is *not* in the US or Canada)
   * @param  {GeocodeFeature} feature
   */
  const formatPlaceName = (feature: GeocodeFeature) => {
    const name = feature.text;

    if (!feature.context) {
      return name;
    }

    const city = getCityFromFeature(feature);
    const country = getCountryFromFeature(feature);
    const stateNames = getStateFromFeature(feature, country);

    // It's nice to display the entire state name if we have space.
    // If the name + city is 16 chars or shorter, use full state name. Otherwise abbreviate
    const baseName = uniq(compact([name, city])).join(', ');
    const state = baseName.length > 16 ? stateNames?.shortName : stateNames?.fullName;

    return uniq(compact([
      name,
      city,
      state,
      country,
    ])).join(', ');
  };

  return sortBy(map(result.body.features, (feature) => (
    {
      ...feature,
      place_name: formatPlaceName(feature),
      sortableType: feature.place_type?.[0],
    }
  )), (f) => (f.sortableType === 'place' ? -1 : 1));
};
